import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ReactNode, useContext } from 'react';
import { colors, spacing } from '../../../utils/styleguide';
import { ppcColors } from '../../../domains/ppc/ppcStyleguide';
import CheckoutContext from '../../../utils/checkoutContext';

interface LabelProps {
  isSelected: boolean;
  colorTheme?: 'default' | 'dark' | 'ppc' | 'custom';
  color?: string;
  backgroundColor?: string;
}

const getThemeCss = (theme?: string, color?: string, backgroundColor?: string) => {
  switch (theme) {
    case 'ppc':
      return {
        border: `1px solid ${ppcColors.primary}`,
        color: colors.black,
        backgroundColor: ppcColors.lightPrimary,
      };
    case 'dark':
      return {
        color: colors.white,
        backgroundColor: colors.black,
      };
    case 'custom':
      return {
        border: `1px solid ${backgroundColor}`,
        color,
        backgroundColor: `${backgroundColor}80`,
      };
    default:
      return {
        color: colors.white,
        backgroundColor: colors.purple400,
      };
  }
};

const Label = styled.label<LabelProps>`
  height: 56px;
  width: 33%;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  text-align: center;
  justify-content: center;

  padding: 10px ${spacing[1]}px;
  cursor: pointer;

  ${({ isSelected, colorTheme, color, backgroundColor }) =>
    isSelected
      ? getThemeCss(colorTheme, color, backgroundColor)
      : css`
          background-color: ${colors.lightGrey};
          color: ${colors.darkGrey};
        `}
`;

type Props = {
  amount?: number;
  onSelectAmount: () => void;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
};

export default function AmountOption(props: Props & LabelProps): JSX.Element {
  const { isSelected, amount, onSelectAmount, children, className, disabled } = props;
  const { color, textColor } = useContext(CheckoutContext);
  const colorTheme =
    props.colorTheme === 'ppc' && (color || textColor) ? 'custom' : props.colorTheme;

  return (
    <Label
      isSelected={isSelected}
      className={className}
      colorTheme={colorTheme}
      color={textColor}
      backgroundColor={color}
    >
      <input
        type="radio"
        name="amount"
        value={amount}
        checked={isSelected}
        onChange={onSelectAmount}
        css={css`
          display: none;
        `}
        disabled={disabled}
      />
      {children}
    </Label>
  );
}
